"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as React from "react";

import { cn } from "lib/utils";

interface RadioGroupProps extends React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Root
> {
  label?: React.ReactNode;
  subLabel?: React.ReactNode;
  error?: string;
  touched?: boolean;
  className?: string;
  classNameParent?: string;
}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(
  (
    {
      className,
      classNameParent = "",
      label,
      subLabel,
      error,
      touched,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={`${classNameParent}`}>
        {label && <h6 className={`text-base font-semibold mb-1 `}>{label}</h6>}
        {subLabel && <p className={`text-sm  `}>{subLabel}</p>}
        <RadioGroupPrimitive.Root
          className={cn(
            `flex flex-col gap-3 ${
              error &&
              touched &&
              "border border-de+1 rounded-md p-2 bg-de-3 text-de+1"
            }`,
            className,
          )}
          {...props}
          ref={ref}
        />
        {error && touched && (
          <p className="text-xs text-de+1 mt-1 font-normal text-left">
            {error}
          </p>
        )}
      </div>
    );
  },
);
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    description?: string;
    classNameParent?: string;
  }
>(({ className, description, classNameParent, ...props }, ref) => {
  return (
    <div className={cn("flex items-center gap-2", classNameParent)}>
      <RadioGroupPrimitive.Item
        ref={ref}
        className={cn(
          "aspect-square h-4 w-4 sm:h-5 sm:w-5  rounded-full border border-gr shadow focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 bg-white",
          className,
        )}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <div className="h-2.5 w-2.5 sm:h-3 sm:w-3 bg-gr rounded-full"></div>
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {description && <p>{description}</p>}
    </div>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
